import type { ComponentType } from "react";
import { TopNav } from "./components/TopNav";
import { Hero } from "./components/Hero";
import { Philosophy } from "./components/Philosophy";
import { Funnel } from "./components/Funnel";
import { Environment } from "./components/Environment";
import { Apps } from "./components/Apps";
import { Layouts } from "./components/Layouts";
import { LocalFirst } from "./components/LocalFirst";
import { Download } from "./components/Download";
import { Footer } from "./components/Footer";
import { InviteAccept } from "./components/InviteAccept";
import { BrowserPage } from "./pages/BrowserPage";
import { CalculatorPage } from "./pages/CalculatorPage";
import { ChatPage } from "./pages/ChatPage";
import { LayoutsPage } from "./pages/LayoutsPage";
import { MailPage } from "./pages/MailPage";
import { MoviesPage } from "./pages/MoviesPage";
import { MusicPage } from "./pages/MusicPage";
import { NotesPage } from "./pages/NotesPage";
import { KeplerPage } from "./pages/KeplerPage";
import { SettingsPage } from "./pages/SettingsPage";
import { StickiesPage } from "./pages/StickiesPage";
import { TerminalPage } from "./pages/TerminalPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";

/**
 * Atlas Desk single-page marketing site. Cloudflare Pages falls back
 * to /index.html for arbitrary subpaths via public/_redirects, so
 * routing is done client-side by reading window.location.pathname
 * once at mount and looking it up in {@link ROUTES}.
 *
 * Two flavours of route exist:
 *   1. Static-path routes registered in {@link ROUTES} — pure lookup.
 *      Add a new per-app marketing page by dropping an entry in.
 *   2. Parameterised routes (currently just /chat/i/<inviteId>) —
 *      checked before the lookup table via {@link matchInvitePath}.
 *
 * Intentionally no React Router — the site stays shallow. A 20-line
 * dispatcher keeps the dep count at zero.
 */

const ROUTES: Record<string, ComponentType> = {
  "/": HomePage,
  "/browser": BrowserPage,
  "/calculator": CalculatorPage,
  "/chat": ChatPage,
  "/layouts": LayoutsPage,
  "/mail": MailPage,
  "/movies": MoviesPage,
  "/music": MusicPage,
  "/notes": NotesPage,
  "/kepler": KeplerPage,
  "/settings": SettingsPage,
  "/stickies": StickiesPage,
  "/terminal": TerminalPage,
  "/terms": TermsPage,
  "/privacy": PrivacyPage,
};

export function App() {
  const inviteId = matchInvitePath();
  if (inviteId !== null) {
    return <InviteAccept inviteId={inviteId} />;
  }

  const path = readPath();
  const Page = ROUTES[path] ?? HomePage;
  return <Page />;
}

/**
 * Pulls the inviteId off `/chat/i/<inviteId>` (with or without a
 * trailing slash). Returns null for any other path so the marketing
 * homepage renders.
 */
function matchInvitePath(): string | null {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/^\/chat\/i\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]!) : null;
}

/**
 * Returns the current path, normalised: trailing slash stripped (so
 * `/stickies` and `/stickies/` resolve to the same entry), and
 * empty becomes `/`. SSR-safe — returns `/` if `window` is absent.
 */
function readPath(): string {
  if (typeof window === "undefined") return "/";
  const raw = window.location.pathname;
  if (raw === "/") return "/";
  return raw.replace(/\/+$/, "") || "/";
}

function HomePage() {
  return (
    <>
      <TopNav />
      <main id="top">
        <Hero />
        <Philosophy />
        <Environment />
        <Apps />
        <Layouts />
        <Funnel />
        <LocalFirst />
        <Download />
      </main>
      <Footer />
    </>
  );
}
